import { toast } from "sonner";
import { Copy, PencilLine, Plus, Clock, FileText, Timer } from "lucide-react";
import MissionShell from "../MissionShell";
import { Card, Field, Btn, BtnLink } from "@/components/kit";
import { HOOKS } from "../content";
import { useProgress } from "../store";
import { isDone, REQUIREMENTS, assembleScript, scriptStats } from "../logic";
import { copyText } from "@/lib/clipboard";

export async function copyScript(script) {
  if (!script) return toast("Ton script est encore vide.");
  (await copyText(script)) ? toast.success("Script copié dans le presse-papier") : toast.error("La copie n'a pas fonctionné. Sélectionne le texte manuellement.");
}

export function ScriptStats({ script }) {
  const st = scriptStats(script);
  return (
    <p className="flex items-center gap-2 text-sm text-soft" data-testid="script-length">
      <Clock className="h-4 w-4 text-gold" strokeWidth={1.6} />
      {st.words} mots · environ {st.seconds} s — {st.label}
    </p>
  );
}

export default function Mission4() {
  const { state, update, setCompleted } = useProgress();
  const s = state.m4;
  const set = (k) => (v) => update("m4", { [k]: v });
  const script = assembleScript(s);
  const edit = () => {
    const el = document.getElementById("script-hook");
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
    setTimeout(() => el?.focus(), 350);
  };
  return (
    <MissionShell id={4} done={isDone(state, 4)} validate={{ missing: REQUIREMENTS[4](state), onClick: () => { setCompleted(4, true); toast.success("Mission 04 validée !"); } }}>
      <div className="space-y-5">
        <Card>
          <p className="text-[15px] font-medium text-ink">Trois accroches à adapter</p>
          <p className="mt-1 text-sm text-soft">Ajoute un modèle à ton accroche, puis complète-le avec ton produit.</p>
          <div className="mt-4 space-y-2">
            {HOOKS.map((h, i) => (
              <div key={h} className="flex flex-col gap-2 rounded-2xl border border-line px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="font-display italic text-bx">« {h.trim()} »</p>
                <Btn variant="ghost" size="sm" onClick={() => set("hook")((s.hook ? s.hook.trimEnd() + " " : "") + h)} data-testid={`use-hook-${i}`}><Plus className="h-4 w-4" />Utiliser</Btn>
              </div>
            ))}
          </div>
        </Card>
        <Card className="space-y-5">
          <Field inputId="script-hook" label="1. Accroche" hint="Une phrase qui attire l'attention." value={s.hook} onChange={set("hook")} multiline rows={2} testId="script-hook-input" />
          <Field label="2. Démonstration" hint="La présentation et l'utilisation du produit. Décris ce que tu fais réellement." value={s.demo} onChange={set("demo")} multiline rows={5} testId="script-demo-input" />
          <Field label="3. Conclusion" hint="L'idée à retenir, en une phrase." value={s.conclusion} onChange={set("conclusion")} multiline rows={2} testId="script-conclusion-input" />
          <p className="text-xs text-soft">Enregistrement automatique dans ton compte.</p>
        </Card>
        <div className="rounded-[20px] bg-bx p-6 text-white sm:p-7" data-testid="final-script-card">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold">Mon script final</p>
          <p className="mt-4 min-h-[60px] whitespace-pre-line font-display text-lg leading-relaxed" data-testid="final-script-text">{script || "Ton script s'assemblera ici au fil de ton écriture."}</p>
          <div className="mt-5 rounded-xl bg-white/10 px-3 py-2 [&_p]:text-white/85 [&_svg]:text-gold"><ScriptStats script={script} /></div>
          <div className="mt-5 flex flex-wrap gap-3">
            <Btn variant="light" size="sm" onClick={() => copyScript(script)} data-testid="copy-script-button"><Copy className="h-4 w-4" />Copier le script</Btn>
            <Btn variant="light" size="sm" onClick={edit} className="!bg-transparent !text-white ring-1 ring-white/40 hover:!bg-white/10" data-testid="edit-script-button"><PencilLine className="h-4 w-4" />Modifier le script</Btn>
            <BtnLink to="/script/imprimer" variant="light" size="sm" className="!bg-transparent !text-white ring-1 ring-white/40 hover:!bg-white/10" data-testid="script-pdf-link"><FileText className="h-4 w-4" />Mon script en PDF</BtnLink>
            <BtnLink to="/script/repeter" variant="gold" size="sm" data-testid="script-rehearse-link"><Timer className="h-4 w-4" />Répéter avec le minuteur</BtnLink>
          </div>
        </div>
      </div>
    </MissionShell>
  );
}
